import { useEffect, useState } from 'react';

import css from './CourseSchedule.module.css';

import toShort from '../../../../../services/utils/dateParser';
import getWeekDay from '../../../../../services/utils/getWeekDay';
import PlusButton from '../../../_common/elements/PlusButton';

import * as scheduleServices from '../../../../../services/courseScheduleServices';
import * as staffServices from '../../../../../services/staffServices';
import { useNavigate } from 'react-router-dom';

function CourseSchedule({ courseDetails }) {
    const [schedule, setSchedule] = useState([]);
    const [editRow, setEditRow] = useState(null);
    const [editedData, setEditedData] = useState({});
    const [staffList, setStaffList] = useState([]);
    const [moduleList, setModuleList] = useState([]);
    const [ataList, setAtaList] = useState([]);
    const [message, setMessage] = useState('');
    const [totalTime, setTotalTime] = useState('');

    useEffect(() => {
        fetchSchedule();
        if (staffList.length === 0) fetchStaffList();
        if (moduleList.length === 0) fetchModuleList();
        if (ataList.length === 0) fetchAtaList();
    }, [courseDetails]);

    useEffect(() => {
        if (schedule.length > 0) {
            calculateTotalTime();
        }
    }, [schedule]);

    // Fetch functions
    const fetchSchedule = async () => {
        try {
            const result = await scheduleServices.getById(courseDetails[0].courseId);
            result.data.sort((a, b) => new Date(a.courseScheduleDate) - new Date(b.courseScheduleDate));
            setSchedule(result.data);
        } catch (error) {
            console.error('Failed to fetch schedule:', error);
        }
    };

    const fetchStaffList = async () => {
        try {
            const result = await staffServices.getAll(1);
            setStaffList(result.data);
        } catch (error) {
            console.error('Failed to fetch staff list:', error);
        }
    };

    const fetchModuleList = async () => {
        try {
            const result = await scheduleServices.getOption('moduleList');
            setModuleList(result.data);
        } catch (error) {
            console.error('Failed to fetch module list:', error);
        }
    };

    const fetchAtaList = async () => {
        try {
            const result = await scheduleServices.getOption('ataList');
            setAtaList(result.data);
        } catch (error) {
            console.error('Failed to fetch ATA list:', error);
        }
    };

    const calculateTotalTime = () => {
        if (schedule.length > 0) {
            const totalSeconds = schedule.reduce((acc, item) => {
                if (item.duration) {
                    const [hours, minutes] = item.duration.split(':').map(Number);
                    return acc + hours * 3600 + minutes * 60;
                } else {
                    return acc;
                }
            }, 0);

            const hours = Math.floor(totalSeconds / 3600)
                .toString()
                .padStart(2, '0');
            const minutes = Math.floor((totalSeconds % 3600) / 60)
                .toString()
                .padStart(2, '0');
            setTotalTime(`${hours}:${minutes}`);
        }
    };

    // Optimistic Updates
    const addRow = (newRow) => {
        setSchedule((prev) => [...prev, newRow]);
        // fetchSchedule();
    };

    const updateRow = (id, updatedRow) => {
        console.log(updatedRow);

        setSchedule((prev) => prev.map((row) => (row.courseScheduleId === id ? updatedRow : row)));
    };

    const deleteRow = (id) => {
        setSchedule((prev) => prev.filter((row) => row.courseScheduleId !== id));
    };

    const handleSave = async (id) => {
        const isNewRow = id === 'new';
        const rowData = { ...editedData, courseId: courseDetails[0].courseId };

        if (isNewRow) {
            //   addRow(rowData); // Optimistic UI update
        } else {
            updateRow(id, rowData); // Optimistic UI update
        }

        try {
            if (isNewRow) {
                const result = await scheduleServices.create(rowData);
                setMessage('Row created successfully! New ID: ' + result.insertId);
                fetchSchedule(); // Reload the data on error
                // updateRow("new", {...editedData, courseScheduleId: result.insertId}); // Replace the temporary row with the real row data from the server
                // updateRow("new", result.data); // Replace the temporary row with the real row data from the server
            } else {
                rowData.courseScheduleDate = toShort(rowData.courseScheduleDate, 1);
                await scheduleServices.edit({ ...rowData, _id: id });
                setMessage('Row edited successfully!');
                fetchSchedule();
            }
        } catch (error) {
            setMessage(`Error: ${error.message}`);
            console.error(error);
            if (isNewRow) {
                deleteRow('new'); // Rollback if the new row creation fails
            } else {
                fetchSchedule(); // Reload the data on error
            }
        } finally {
            setEditRow(null);
        }
    };

    const handleDelete = async (id) => {
        deleteRow(id); // Optimistic UI update
        try {
            await scheduleServices.del(id);
            setMessage('Row deleted successfully!');
        } catch (error) {
            setMessage(`Error: ${error.message}`);
            console.error(error);
            fetchSchedule(); // Reload the data on error
        }
    };

    const handleAdd = () => {
        let newRow = {};
        if (schedule.length) {
            newRow = { ...schedule[schedule.length - 1] };
            newRow.courseScheduleId = 'new';
            newRow.courseScheduleDate = toShort(newRow.courseScheduleDate, 1);
        } else {
            newRow.courseScheduleId = 'new';
        }
        addRow(newRow);
        setEditRow('new');
        setEditedData(newRow);
    };

    const handleChange = (field, value) => {
        setEditedData((prev) => ({ ...prev, [field]: value }));
    };

    const handleEdit = (id, row) => {
        setEditRow(id);
        row.courseScheduleDate = toShort(row.courseScheduleDate, 1);
        setEditedData(row);
    };

    return (
        <div>
            <h3>Course Schedule</h3>
            <table className={css.table}>
                <thead className={css.thead}>
                    <tr className={css.tr}>
                        <th className={css.th} style={{ width: '3rem' }}>
                            Day
                        </th>
                        <th className={css.th} style={{ width: '8rem' }}>
                            Date
                        </th>
                        <th className={css.th} style={{ width: '20rem' }}>
                            Subject
                        </th>
                        <th className={css.th} style={{ width: '4rem' }}>
                            Time
                        </th>
                        <th className={css.th} style={{ width: '4rem' }}>
                            Module
                        </th>
                        <th className={css.th} style={{ width: '9rem' }}>
                            Instructor
                        </th>
                        <th className={css.th} style={{ width: '3rem' }}>
                            Actions
                        </th>
                    </tr>
                </thead>
                <tbody className={css.tbody}>
                    {schedule.map((row) => (
                        <tr key={row.courseScheduleId} className={css.tr}>
                            {editRow === row.courseScheduleId ? (
                                <EditableRow
                                    data={editedData}
                                    staffList={staffList}
                                    moduleList={moduleList}
                                    ataList={ataList}
                                    onChange={handleChange}
                                    onSave={() => handleSave(row.courseScheduleId)}
                                    onCancel={() => setEditRow(null)}
                                />
                            ) : (
                                <NonEditableRow
                                    data={row}
                                    onEdit={() => handleEdit(row.courseScheduleId, row)}
                                    onDelete={() => handleDelete(row.courseScheduleId)}
                                />
                            )}
                        </tr>
                    ))}
                </tbody>
            </table>
            <PlusButton onClick={handleAdd} />
            <p>Total Time: {totalTime}</p>
            <p>{message}</p>
        </div>
    );
}

function EditableRow({ data, staffList, moduleList, ataList, onChange, onSave, onCancel }) {
    return (
        <>
            <td className={css.td}>{getWeekDay(data.courseScheduleDate)}</td>
            <td className={css.td}>
                <input
                    type="date"
                    style={{ width: '7rem' }}
                    value={data.courseScheduleDate || ''}
                    onChange={(e) => onChange('courseScheduleDate', e.target.value)}
                />
            </td>
            <td className={css.td}>
                <select value={data.ataId || ''} style={{ width: '18rem' }} onChange={(e) => onChange('ataId', e.target.value)}>
                    <option value="">Select subject</option>
                    {ataList.map((ata) => (
                        <option key={ata.ataId} value={ata.ataId}>
                            {ata.ataNo} {ata.ataTitle}
                        </option>
                    ))}
                </select>
            </td>
            <td className={css.td}>
                <input
                    type="text"
                    style={{ width: '3rem' }}
                    value={data.duration || ''}
                    onChange={(e) => onChange('duration', e.target.value)}
                />
            </td>
            <td className={css.td}>
                <select
                    value={data.programModuleId || ''}
                    style={{ width: '3rem' }}
                    onChange={(e) => onChange('programModuleId', e.target.value)}
                >
                    <option value="">Select module</option>
                    {moduleList.map((module) => (
                        <option key={module.programModuleId} value={module.programModuleId}>
                            {module.programModuleShort}
                        </option>
                    ))}
                </select>
            </td>
            <td className={css.td}>
                <select value={data.staffId || ''} style={{ width: '7rem' }} onChange={(e) => onChange('staffId', e.target.value)}>
                    <option value="">Select Instructor</option>
                    {staffList.map((staff) => (
                        <option key={staff.ID} value={staff.ID}>
                            {staff.Name}
                        </option>
                    ))}
                </select>
            </td>
            <td className={css.functionButtons}>
                <i className={`fa-solid fa-floppy-disk ${css.green}`} onClick={onSave}></i>
                <i className={`fa-solid fa-ban ${css.red}`} onClick={onCancel}></i>
            </td>
        </>
    );
}

function NonEditableRow({ data, onEdit, onDelete }) {
    const navigate = useNavigate();

    return (
        <>
            <td className={css.td}>{getWeekDay(data.courseScheduleDate)}</td>
            <td className={css.td}>{toShort(data.courseScheduleDate)}</td>
            <td className={css.td}>
                {data.ataNo} {data.ataTitle}
            </td>
            <td className={css.td}>{data.duration}</td>
            <td className={css.td}>{data.module}</td>
            <td className={css.tdLink} onClick={() => navigate(`/staff/details/${data.staffId}`)}>
                {data.staffName}
            </td>
            <td className={css.functionButtons}>
                <i className={`fa-solid fa-pen ${css.orange}`} onClick={onEdit}></i>
                <i className={`fa-solid fa-trash ${css.red}`} onClick={onDelete}></i>
            </td>
        </>
    );
}

export default CourseSchedule;

// import { useEffect, useState } from 'react';

// import css from './CourseSchedule.module.css';

// import toShort from '../../../../../services/utils/dateParser';
// import getWeekDay from '../../../../../services/utils/getWeekDay';
// import PlusButton from '../../../_common/elements/PlusButton';

// import * as scheduleServices from '../../../../../services/courseScheduleServices';
// import * as staffServices from '../../../../../services/staffServices';

// function CourseSchedule({ courseDetails }) {
//     const [schedule, setSchedule] = useState([]);
//     const [editRow, setEditRow] = useState(null); // Track which row is being edited
//     const [editedData, setEditedData] = useState({}); // Track data being edited
//     const [staffList, setStaffList] = useState([]);
//     const [moduleList, setModuleList] = useState([]);
//     const [ataList, setAtaList] = useState([]);
//     const [message, setMessage] = useState('');
//     const [totalTime, setTotalTime] = useState('');

//     // console.log(editedData);

//     useEffect(() => {
//         scheduleServices
//             .getById(courseDetails[0].courseId)
//             .then((result) => {
//                 // result.data = result.data.sort((a, b) => a.studentName.localeCompare(b.studentName));
//                 result.data.sort((a, b) => new Date(a.courseScheduleDate) - new Date(b.courseScheduleDate));
//                 setSchedule((state) => result.data);
//             })
//             .catch((error) => {
//                 console.log(error);
//             });

//         if (staffList.length === 0) {
//             let page;
//             staffServices
//                 .getAll(page || 1)
//                 .then((result) => {
//                     setStaffList((state) => result.data);
//                 })
//                 .catch((error) => {
//                     console.log(error);
//                 });
//         }

//         if (moduleList.length === 0) {
//             scheduleServices
//                 .getOption('moduleList')
//                 .then((result) => {
//                     setModuleList((state) => result.data);
//                 })
//                 .catch((error) => {
//                     console.log(error);
//                 });
//         }

//         if (ataList.length === 0) {
//             scheduleServices
//                 .getOption('ataList')
//                 .then((result) => {
//                     setAtaList((state) => result.data);
//                 })
//                 .catch((error) => {
//                     console.log(error);
//                 });
//         }
//     }, [courseDetails]);

//     useEffect(() => {
//         let totalTimeInSecs = 0;
//         schedule.forEach((x) => {
//             let ms = x.duration.split(':');
//             let secs = +ms[0] * 3600 + +ms[1]*60;
//             totalTimeInSecs = totalTimeInSecs + secs;
//         });
//         console.log(totalTimeInSecs);

//         const date = new Date(null);
//         date.setSeconds(totalTimeInSecs); // specify value for SECONDS here
//         setTotalTime(date.toISOString().slice(11, 19));
//         // setTotalTime(new Date(totalTimeInSecs * 1000).toISOString().slice(11, 19))
//     }, [schedule]);

//     const handleEdit = (index, row) => {
//         setEditRow(index); // Set the row to be editable
//         setEditedData(row); // Initialize with the current row data
//     };

//     const handleChange = (field, value) => {
//         setEditedData((prev) => ({
//             ...prev,
//             [field]: value,
//         }));
//     };

//     const handleSave = (courseScheduleId) => {
//         if (courseScheduleId === 'new') {
//             console.log('Adding row:', courseScheduleId, editedData); // Add logic to save the new data
//             let data = editedData;
//             data.courseId = courseDetails[0].courseId;
//             scheduleServices
//                 .create(editedData)
//                 .then((result) => {
//                     console.log(result.message);
//                     setMessage('Row created successfully!');
//                     setEditRow(null); // Exit edit mode
//                 })
//                 .catch((error) => {
//                     console.log(error);
//                     setMessage('Error: ' + error);
//                 });
//         } else {
//             console.log('Saving row:', courseScheduleId, editedData); // Add logic to save edited data
//             let data = editedData;
//             data._id = courseScheduleId;
//             scheduleServices
//                 .edit(editedData)
//                 .then((result) => {
//                     console.log(result.message);
//                     setEditRow(null); // Exit edit mode

//                     let editedElement = schedule.filter((x) => x.courseScheduleId === courseScheduleId)[0];
//                     editedElement.ataNo =
//                         ataList.filter((x) => x.ataId === editedElement.ataId)[0].ataNo +
//                         ataList.filter((x) => x.ataId === editedElement.ataId)[0].ataTitle;
//                     setMessage('Row edited successfully!');
//                 })
//                 .catch((error) => {
//                     console.log(error);
//                     setMessage('Error: ' + error);
//                 });
//         }
//     };

//     const handleCancel = () => {
//         setEditRow(null); // Exit edit mode without saving
//         setEditedData(null);
//     };

//     const handleAdd = () => {
//         let lastElement = { ...schedule[schedule.length - 1] };
//         lastElement.courseScheduleId = 'new';
//         lastElement.courseScheduleDate = toShort(lastElement.courseScheduleDate, 1);
//         console.log(lastElement);

//         setSchedule((state) => [
//             ...state,
//             lastElement,
//             // { courseScheduleId: 'new', courseScheduleDate: null, ataId: null, duration: null, programModuleId: null, staffId: schedule[schedule.length-1].staffId },
//         ]);
//         setEditedData((state) => lastElement);
//         setEditRow((state) => 'new');
//     };

//     const handleDelete = (id) => {
//         console.log(id);

//         scheduleServices
//             .del(id)
//             .then((result) => {
//                 console.log(result.message);
//                 setMessage('Row deleted successfully!');
//             })
//             .catch((error) => {
//                 console.log(error);
//                 setMessage('Error: ' + error);
//             });
//     };

//     // const timeParser = (duration) => {
//     //     let hours = Math.floor(duration);
//     //     let minutes = duration - hours;
//     //     const leadingZeroes = (num) => {
//     //         let numStr = num.toString();
//     //         while (numStr.length < 2) {
//     //             numStr = '0' + numStr;
//     //         }
//     //         return numStr;
//     //     };
//     //     return leadingZeroes(hours) + ':' + leadingZeroes(0.6 * Math.floor(minutes * 100));
//     // };

//     return (
//         <div>
//             <h3>Course Schedule</h3>
//             <table className={css.table}>
//                 <thead className={css.thead}>
//                     <tr>
//                         <th className={css.th}>Day</th>
//                         <th className={css.th}>Date</th>
//                         <th className={css.th}>Subject</th>
//                         <th className={css.th}>Time</th>
//                         <th className={css.th}>Module</th>
//                         <th className={css.th}>Instructor</th>
//                         <th className={css.th}></th>
//                     </tr>
//                 </thead>
//                 <tbody className={css.tbody}>
//                     {schedule.map((x, index) => (
//                         <tr key={x.scheduleId} className={css.tr}>
//                             {/* Editable Row */}
//                             {editRow === x.courseScheduleId ? (
//                                 <>
//                                     <td className={css.td}>{getWeekDay(editedData.courseScheduleDate)}</td>
//                                     <td className={css.td}>
//                                         <input
//                                             type="date"
//                                             style={{ width: '7rem' }}
//                                             value={toShort(editedData.courseScheduleDate, 1) || ''}
//                                             onChange={(e) => handleChange('courseScheduleDate', e.target.value)}
//                                         />
//                                     </td>
//                                     <td className={css.td}>
//                                         <select
//                                             type="text"
//                                             style={{ width: '18rem' }}
//                                             value={editedData.ataId || ''}
//                                             onChange={(e) => handleChange('ataId', e.target.value)}
//                                         >
//                                             <option value="">Select subject</option>
//                                             {ataList.map((ata) => (
//                                                 <option key={ata.ataId} value={ata.ataId}>
//                                                     {ata.ataNo} {ata.ataTitle}
//                                                 </option>
//                                             ))}
//                                         </select>
//                                     </td>
//                                     <td className={css.td}>
//                                         <input
//                                             type="text"
//                                             style={{ width: '3rem' }}
//                                             value={editedData.duration || ''}
//                                             onChange={(e) => handleChange('duration', e.target.value)}
//                                         />
//                                     </td>
//                                     <td className={css.td}>
//                                         <select
//                                             style={{ width: '2rem' }}
//                                             value={editedData.programModuleId || ''}
//                                             onChange={(e) => handleChange('programModuleId', e.target.value)}
//                                         >
//                                             <option value="">Select module</option>
//                                             {moduleList.map((module) => (
//                                                 <option key={module.programModuleId} value={module.programModuleId}>
//                                                     {module.programModuleShort}
//                                                 </option>
//                                             ))}
//                                         </select>
//                                     </td>
//                                     <td className={css.td}>
//                                         <select
//                                             style={{ width: '7rem' }}
//                                             value={editedData.staffId || ''}
//                                             onChange={(e) => handleChange('staffId', e.target.value)}
//                                         >
//                                             <option value="">Select Instructor</option>
//                                             {staffList.map((staff) => (
//                                                 <option key={staff.ID} value={staff.ID}>
//                                                     {staff.Name}
//                                                 </option>
//                                             ))}
//                                         </select>
//                                     </td>
//                                     <td className={css.functionButtons}>
//                                         <i className={`fa-solid fa-check ${css.green}`} onClick={() => handleSave(x.courseScheduleId)}></i>
//                                         <i className={`fa-solid fa-times ${css.red}`} onClick={handleCancel}></i>
//                                     </td>
//                                 </>
//                             ) : (
//                                 // Non-Editable Row
//                                 <>
//                                     <td className={css.td}>{getWeekDay(x.courseScheduleDate)}</td>
//                                     <td className={css.td}>{toShort(x.courseScheduleDate)}</td>
//                                     <td className={css.td}>
//                                         {x.ataNo} {x.ataTitle}
//                                     </td>
//                                     <td className={css.td}>{x.duration}</td>
//                                     <td className={css.td}>{x.module}</td>
//                                     <td className={css.td}>{x.staffName}</td>
//                                     <td className={css.functionButtons}>
//                                         <i
//                                             className={`fa-solid fa-pen ${css.orange}`}
//                                             onClick={() =>
//                                                 handleEdit(x.courseScheduleId, {
//                                                     courseScheduleDate: toShort(x.courseScheduleDate, 1),
//                                                     ataId: x.ataId,
//                                                     duration: x.duration,
//                                                     programModuleId: x.programModuleId,
//                                                     staffId: x.staffId,
//                                                 })
//                                             }
//                                         ></i>
//                                         <i className={`fa-solid fa-trash ${css.red}`} onClick={() => handleDelete(x.courseScheduleId)}></i>
//                                     </td>
//                                 </>
//                             )}
//                         </tr>
//                     ))}
//                 </tbody>
//             </table>
//             <PlusButton onClick={handleAdd} />
//             <p>{totalTime}</p>
//             <p>{message}</p>
//         </div>
//     );
// }

// export default CourseSchedule;
